import { FunctionComponent } from 'react';
import AnimatedProgressBar, {
  ProgressSegment,
} from '../../../ui/AnimatedProgressBar';
import {
  FaExclamationTriangle,
  FaCheckCircle,
  FaChartLine,
} from 'react-icons/fa';
import { StatusPillGroup } from './StatusPill';
import { AggregateSummary } from '../types/dashboard.types';
import { tailwindColors } from '../../../../../tailwind.config';

interface PassRateProps {
  percentage: number;
  segments: ProgressSegment[];
  totals: AggregateSummary;
}

const PassRate: FunctionComponent<PassRateProps> = ({
  percentage,
  segments,
  totals,
}) => {
  // Check if percentage is NaN (no data available)
  const isNaN = Number.isNaN(percentage);

  // Determine color and icon based on pass rate
  const isPassing = percentage >= 90;
  const IconComponent = isNaN
    ? FaCheckCircle
    : isPassing
      ? FaCheckCircle
      : FaExclamationTriangle;
  const total = segments.reduce((sum, d) => sum + d.value, 0);

  return (
    <div className="w-full rounded-md bg-gray-50/50 p-4">
      <div className="flex flex-col gap-2">
        <div>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
              <IconComponent
                className={
                  isNaN
                    ? 'text-gray-400'
                    : isPassing
                      ? 'text-pass'
                      : 'text-fail'
                }
              />
              Pass Rate
            </div>

            <div className="text-xs text-gray-400">Within chosen period</div>
          </div>
          <div className="mt-4">
            {isNaN && total === 0 ? (
              <div className="flex flex-col items-center justify-center py-4">
                <div className="mb-1 text-sm text-gray-500">
                  No reports calculated
                </div>
                <div className="text-center text-xs text-gray-400">
                  in this time frame
                </div>
              </div>
            ) : (
              <>
                <div
                  className={`text-lg font-bold tracking-tight ${
                    isNaN
                      ? 'text-gray-500'
                      : isPassing
                        ? 'text-gray-800'
                        : 'text-fail'
                  }`}
                >
                  {isNaN ? 'N/A' : `${percentage.toFixed(1)}%`}
                </div>
                {total > 0 && (
                  <>
                    <div className="mt-2 flex-1">
                      <AnimatedProgressBar segments={segments} total={total} />
                    </div>

                    {/* Status Labels - only show when there's data */}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <StatusPillGroup data={totals.totalFails} />
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PassRate;
